import * as pc from "playcanvas";

import type { Number2 } from "../types/Number";
import { isApproximately } from "./Number";

export interface ReadonlyVector2 {
  get x(): number;
  get y(): number;

  angle(): number;
  angleTo(rhs: ReadonlyVector2): number;
  clone(): Vector2;
  copyToArray(array: Array<number> | Float32Array | Float64Array, offset?: number): this;
  cross(rhs: ReadonlyVector2): number;
  distance(rhs: ReadonlyVector2): number;
  dot(rhs: ReadonlyVector2): number;
  equals(rhs: ReadonlyVector2): boolean;
  equalsApprox(rhs: ReadonlyVector2, epsilon?: number): boolean;
  length(): number;
  lengthSq(): number;
  toString(): string;
  toArray(): Number2;

  // Extensions
  angleRad(rhs: ReadonlyVector2): number;
  distanceSq(rhs: ReadonlyVector2): number;
  isApproximately(rhs: ReadonlyVector2): boolean;
  isFinite(): boolean;

  // Convert for use in external JS code.
  // This is unsafe and should only be used when necessary.
  unsafeCast(): Vector2;
}

declare module "playcanvas" {
  interface Vec2 extends ReadonlyVector2 {
    x: number;
    y: number;

    add(rhs: ReadonlyVector2): this;
    add2(lhs: ReadonlyVector2, rhs: ReadonlyVector2): this;
    addScalar(scalar: number): this;
    addScaled(rhs: ReadonlyVector2, scalar: number): this;
    angleTo(rhs: ReadonlyVector2): number;
    ceil(src?: ReadonlyVector2): this;
    copy(rhs: ReadonlyVector2): this;
    copyToArray(array: Array<number> | Float32Array | Float64Array, offset?: number): this;
    cross(rhs: ReadonlyVector2): number;
    divScalar(scalar: number): this;
    distance(rhs: ReadonlyVector2): number;
    div(rhs: ReadonlyVector2): this;
    div2(lhs: ReadonlyVector2, rhs: ReadonlyVector2): this;
    dot(rhs: ReadonlyVector2): number;
    equals(rhs: ReadonlyVector2): boolean;
    equalsApprox(rhs: ReadonlyVector2, epsilon?: number): boolean;
    floor(src?: ReadonlyVector2): this;
    lerp(lhs: ReadonlyVector2, rhs: ReadonlyVector2, alpha: number): this;
    max(rhs: ReadonlyVector2): this;
    min(rhs: ReadonlyVector2): this;
    mul(rhs: ReadonlyVector2): this;
    mul2(lhs: ReadonlyVector2, rhs: ReadonlyVector2): this;
    mulScalar(scalar: number): this;
    normalize(src?: ReadonlyVector2): this;
    rotate(degrees: number): this;
    round(src?: ReadonlyVector2): this;
    set(x: number, y: number): this;
    sub(rhs: ReadonlyVector2): this;
    sub2(lhs: ReadonlyVector2, rhs: ReadonlyVector2): this;
    subScalar(scalar: number): this;
    toArray(): Number2;

    // Extensions
    angleRad(rhs: ReadonlyVector2): number;
    distanceSq(rhs: ReadonlyVector2): number;
    fromArray(array: Readonly<number[]>, offset?: number): this;
    isApproximately(rhs: ReadonlyVector2): boolean;
    isFinite(): boolean;
    project(rhs: ReadonlyVector2): this;
    recycle(): void;
  }
}

pc.Vec2.prototype.angleRad = function (rhs: ReadonlyVector2) {
  return Math.atan2(this.x * rhs.y - this.y * rhs.x, this.x * rhs.x + this.y * rhs.y);
};

pc.Vec2.prototype.distanceSq = function (rhs: ReadonlyVector2) {
  const x = this.x - rhs.x;
  const y = this.y - rhs.y;
  return x * x + y * y;
};

pc.Vec2.prototype.fromArray = function (array: Readonly<Number2>, offset = 0) {
  this.x = array[offset];
  this.y = array[offset + 1];
  return this;
};

pc.Vec2.prototype.isFinite = function () {
  return Number.isFinite(this.x) && Number.isFinite(this.y);
};

pc.Vec2.prototype.isApproximately = function (rhs) {
  return isApproximately(this.x, rhs.x) && isApproximately(this.y, rhs.y);
};

pc.Vec2.prototype.project = function (rhs: ReadonlyVector2) {
  const a_dot_b = this.x * rhs.x + this.y * rhs.y;
  const b_dot_b = rhs.x * rhs.x + rhs.y * rhs.y;
  const s = a_dot_b / b_dot_b;
  this.x = rhs.x * s;
  this.y = rhs.y * s;
  return this;
};

pc.Vec2.prototype.recycle = function () {
  pool.push(this);
};

pc.Vec2.prototype.unsafeCast = function () {
  return this;
};

const pool: Vector2[] = [];

// @ts-expect-error - Override static constants with ReadonlyVector32 type (intentionally incompatible with base class)
export class Vector2 extends pc.Vec2 {
  static isInstance(value: unknown) {
    return value instanceof pc.Vec2;
  }

  static override ZERO = Vector2.makeReadonly(0, 0);
  static override HALF = Vector2.makeReadonly(0.5, 0.5);
  static override ONE = Vector2.makeReadonly(1, 1);

  static override UP = Vector2.makeReadonly(0, 1);
  static override DOWN = Vector2.makeReadonly(0, -1);
  static override RIGHT = Vector2.makeReadonly(1, 0);
  static override LEFT = Vector2.makeReadonly(-1, 0);

  static fromArray(array: Readonly<Number2>) {
    return this.make(array[0], array[1]);
  }

  /**
   * @summary Creates a vector from a variety of input types.
   */
  static from(json: unknown): Vector2 {
    if (Vector2.isInstance(json)) {
      return json.clone();
    } else if (typeof json === "number") {
      return this.make(json, json);
    } else if (Array.isArray(json) && json.length >= 2 && typeof json[0] === "number" && typeof json[1] === "number") {
      return this.make(json[0], json[1]);
    } else if (
      typeof json === "object" &&
      json !== null &&
      "x" in json &&
      "y" in json &&
      typeof json.x === "number" &&
      typeof json.y === "number"
    ) {
      return this.make(json.x, json.y);
    }

    throw new Error("Invalid input for Vector2.from");
  }

  /**
   * Combines two vectors with a blend factor.
   */
  static lerp(lhs: ReadonlyVector2, rhs: ReadonlyVector2, blend: number): Vector2 {
    return Vector2.make(lhs.x + blend * (rhs.x - lhs.x), lhs.y + blend * (rhs.y - lhs.y));
  }

  /**
   * Creates a new vector with the given components.
   */
  static make(x = 0, y = 0): Vector2 {
    return pool.pop()?.set(x, y) ?? new Vector2(x, y);
  }

  /**
   * Create a readonly Vector2 that cannot be recycled.
   */
  static makeReadonly(x: number, y: number): ReadonlyVector2 {
    return this.make(x, y);
  }

  /**
   * Creates a new uninitialized vector.
   */
  static makeUninitialized(): Vector2 {
    return pool.pop() || new Vector2();
  }
}
